import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import ViewCarouselIcon from '@material-design-icons/svg/filled/view_carousel.svg?react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '../components/ui/button';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTRPC } from '../hooks/useTRPC';
import { cn } from '../lib/utils';
import { TabNavigation } from './appSpotlight/components/TabNavigation';
import { appSpotlightTabs } from './appSpotlight/tabConfig';
import {
  ExperimentsTab,
  ReportingTab,
  SpotlightsTab,
} from './appSpotlight/tabs';
import type {
  AppSpotlightTabKey,
  ExperimentPrefill,
  Spotlight,
} from './appSpotlight/types';
import { useAppSpotlight } from './appSpotlight/useAppSpotlight';

export function AppSpotlightPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'admin',
  });
  const trpc = useTRPC();

  usePageTitle('App Spotlight | Admin');

  const [pageError, setPageError] = useState<string | null>(null);
  const onError = useCallback((message: string) => setPageError(message), []);
  const controller = useAppSpotlight({ trpc, onError });

  const [activeTab, setActiveTab] = useState<AppSpotlightTabKey>('spotlights');
  const [selectedSpotlightId, setSelectedSpotlightId] = useState<string | null>(
    null,
  );
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [experimentPrefill, setExperimentPrefill] =
    useState<ExperimentPrefill | null>(null);
  const [prefillNonce, setPrefillNonce] = useState(0);

  const dirtyRef = useRef(false);
  const handleDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const confirmDiscard = () =>
    !dirtyRef.current ||
    window.confirm('Discard unsaved changes to this spotlight?');

  const selectSpotlight = (id: string) => {
    if (!confirmDiscard()) {
      return;
    }
    setSelectedSpotlightId(id);
    setIsCreatingNew(false);
  };

  const startNewSpotlight = () => {
    if (!confirmDiscard()) {
      return;
    }
    setIsCreatingNew(true);
    setSelectedSpotlightId(null);
    setActiveTab('spotlights');
  };

  const handleSaved = (spotlight: Spotlight) => {
    setSelectedSpotlightId(spotlight.id);
    setIsCreatingNew(false);
  };

  const handleRunExperiment = (prefill: ExperimentPrefill) => {
    setExperimentPrefill(prefill);
    setPrefillNonce((current) => current + 1);
    setActiveTab('experiments');
  };

  if (!canRender || authState.type !== 'Authenticated') {
    return null;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="border-b border-neutral-800 bg-neutral-900">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
              <ViewCarouselIcon className="size-5 fill-current text-white" />
              <div>
                <h1 className="text-base font-semibold text-white">
                  App Spotlight
                </h1>
                <p className="text-xs text-neutral-500">
                  Weekly editorial curation, personalization experiments, and
                  telemetry.
                </p>
              </div>
            </div>
            {activeTab === 'spotlights' && (
              <Button
                onClick={startNewSpotlight}
                size="sm"
                className="bg-teal-600 text-white hover:bg-teal-500"
              >
                <AddIcon className="size-4 fill-current" />
                New spotlight
              </Button>
            )}
          </div>
          <TabNavigation
            tabs={appSpotlightTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-4 px-4 py-4 md:px-8">
        {pageError && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <span>{pageError}</span>
            <button
              type="button"
              aria-label="Dismiss error"
              onClick={() => setPageError(null)}
              className="shrink-0 text-red-300 hover:text-white"
            >
              <CloseIcon className="size-4 fill-current" />
            </button>
          </div>
        )}

        <div className={cn(activeTab !== 'spotlights' && 'hidden')}>
          <SpotlightsTab
            controller={controller}
            selectedSpotlightId={selectedSpotlightId}
            isCreatingNew={isCreatingNew}
            onSelect={selectSpotlight}
            onSaved={handleSaved}
            onRunExperiment={handleRunExperiment}
            onDirtyChange={handleDirtyChange}
          />
        </div>

        <div className={cn(activeTab !== 'experiments' && 'hidden')}>
          <ExperimentsTab
            key={prefillNonce}
            controller={controller}
            prefill={experimentPrefill}
          />
        </div>

        <div className={cn(activeTab !== 'reporting' && 'hidden')}>
          <ReportingTab controller={controller} />
        </div>
      </div>
    </div>
  );
}
